import { getGoogleLocationIsDuplicate } from './display_helpers';
import { getVerificationAddressString } from './display_helpers';
import { isServiceAreaLocation }        from './display_helpers';
import { useAccessToken }               from './google_auth';
import { useEffect }                    from 'react';
import enums                            from '../../util/enums';
import qs                               from 'query-string';
import useObjectState                   from '../../hooks/use_object_state';

const gmbApiUrl = 'https://mybusiness.googleapis.com/v4';

// https://console.developers.google.com/apis/credentials?project=presence-dev-1236
export const credentials = {
  apiKey:   'AIzaSyCdjFexNPlNktwIFhAqWWfJptx26qpXbZI',
  clientID: '776508127277-d80c57d76okg22610k14n2odfmi0upo8.apps.googleusercontent.com',
  // business.manage:        manage GMB listings
  // businesscommunications: manage Business Messages
  scope:    'https://www.googleapis.com/auth/business.manage https://www.googleapis.com/auth/businesscommunications'
};

// - Locations

export function useAllLocations({ progressStart, progressEnd }) {
  const accessToken            = useAccessToken();
  const [ state, updateState ] = useObjectState({
    result:       [],
    resultStatus: 'pending'
  });

  useEffect(() => {
    if (accessToken) {
      (async() => {
        progressStart();
        try {
          const result = await getAllLocations({ accessToken });
          updateState({
            result,
            resultStatus: 'succeeded'
          });
        } catch (error) {
          // should we do anything with this error?
          updateState({
            resultStatus: 'failed'
          });
        } finally {
          progressEnd();
        }
      })();
    }

    return () => {};
  }, [ accessToken, progressEnd, progressStart, updateState ]);

  return state;
}

export async function getAllLocations({ accessToken }) {

  // fetch account locations
  const accounts         = await fetchAccounts({ accessToken });
  const accountLocations = await fetchAllAccountLocations({ accessToken, accounts });
  const results          = accountLocations.map(mapAccountLocationToResult);

  // just account locations for now. more locations sources to be added here

  return results;
}

export async function fetchAccounts({ accessToken }) {

  const res = await fetchEndpoint({
    accessToken,
    endpoint:  'accounts',
    transform: data => data.accounts || []
  });

  return res;
}

export async function fetchAllAccountLocations({ accessToken, accounts }) {

  const results = await Promise.all(
    accounts
      .filter(account => account && account.name)
      .map(account => fetchAccountLocations({ accessToken, account }))
  );

  const locationArrays = results.map(result => result.locations);

  const locations = [].concat(...locationArrays).filter(filterAllowedLocations);

  return locations;
}

export async function fetchAccountLocations({ accessToken, account, query }) {

  // construct endpoint, including query if provided
  const path        = `${account.name}/locations`;
  const queryString = qs.stringify(query);
  const endpoint    = [ path, queryString ].filter(item => item).join('?');

  const { locations, nextPageToken } = await fetchEndpoint({
    accessToken,
    endpoint
  });

  // if no locations are found, GMB doesn't return the locations property
  if (!Array.isArray(locations))
    return { locations: [] };

  // if nextPageToken is returned, recurse through remaining pages
  if (nextPageToken) {
    const nextPage = await fetchAccountLocations({
      accessToken,
      account,
      query: {
        pageToken: nextPageToken
      }
    });

    if (nextPage && Array.isArray(nextPage.locations))
      locations.push(...nextPage.locations);
  }

  return { locations };

}


// - Verification Options

export const verificationStates = enums([
  'PENDING',
  'COMPLETED',
  'FAILED'
]);

export const verificationMethods = enums([
  'ADDRESS',
  'EMAIL',
  'PHONE_CALL',
  'SMS',
  'AUTO'
]);

const allowedVerificationOptions = [
  verificationMethods.SMS,
  verificationMethods.PHONE_CALL,
  verificationMethods.EMAIL,
  verificationMethods.ADDRESS
];

function validateVerificationOptions(verifyOption) {
  const { verificationMethod } = verifyOption;

  if (!allowedVerificationOptions.includes(verificationMethod))
    return false;

  // only allow address verification if we can derive an address
  if (verificationMethod === verificationMethods.ADDRESS) {
    try {
      getVerificationAddressString(verifyOption);
      return true;
    } catch (error) {
      return false;
    }
  }

  return true;
}

export function useVerificationOptions({
  verificationAddress,
  googleLocation,
  progressStart,
  progressEnd
}) {
  const accessToken            = useAccessToken();
  const [ state, updateState ] = useObjectState({
    options:       [],
    optionsStatus: 'pending'
  });

  useEffect(() => {
    if (accessToken && state.optionsStatus === 'pending') {
      progressStart();
      (async() => {
        try {
          const options = await fetchVerificationOptions({ accessToken, verificationAddress, googleLocation });
          updateState({
            options,
            optionsStatus: 'succeeded'
          });
        } catch (error) {
          updateState({
            optionsStatus: 'failed'
          });
        } finally {
          progressEnd();
        }
      })();
    }

    return () => {};
  }, [
    accessToken,
    verificationAddress,
    googleLocation,
    progressEnd,
    progressStart,
    state.optionsStatus,
    updateState
  ]);

  return state;
}

export async function fetchVerificationOptions({ accessToken, verificationAddress, googleLocation }) {

  const body = getVerificationOptionsBody({ googleLocation, verificationAddress });

  const results = await fetchEndpoint({
    accessToken,
    method:    'POST',
    endpoint:  `${googleLocation.name}:fetchVerificationOptions`,
    transform: data => data.options || [],
    body:      JSON.stringify(body)
  });

  const options = results.filter(validateVerificationOptions);

  return options;
}

export function getVerificationOptionsBody({ googleLocation, verificationAddress }) {

  const languageCode = 'en';

  if (isServiceAreaLocation(googleLocation)) {
    return {
      languageCode,
      context: {
        address: verificationAddress
      }
    };
  } else {
    return {
      languageCode
    };
  }

}


export async function requestVerification({ accessToken, contactName, googleLocation, verifyOption }) {

  const body = getRequestVerificationBody({ contactName, googleLocation, verifyOption });

  const { verification, error } = await fetchEndpoint({
    accessToken,
    method:   'POST',
    endpoint: `${googleLocation.name}:verify`,
    body:     JSON.stringify(body)
  });

  if (error)
    throw new Error(error.message || 'An unknown error occurred.');

  if (!verification)
    throw new Error('No verification returned.');

  if (verification.state !== verificationStates.PENDING)
    throw new Error('Verification is not pending.');

  return verification;
}

export function getRequestVerificationBody({ contactName, googleLocation, verifyOption }) {
  const { verificationMethod } = verifyOption;
  const method                 = verificationMethod;
  const languageCode           = 'en';

  switch (verificationMethod) {

    case verificationMethods.SMS:
    case verificationMethods.PHONE_CALL: {
      const { phoneNumber } = verifyOption.phoneData;
      return {
        method,
        languageCode,
        phoneInput: { phoneNumber }
      };
    }

    case verificationMethods.EMAIL: {
      const { userName }   = verifyOption.emailData;
      const { domainName } = verifyOption.emailData;
      const emailAddress   = `${userName}@${domainName}`;
      return {
        method,
        languageCode,
        emailInput: { emailAddress }
      };
    }

    case verificationMethods.ADDRESS: {
      const body = {
        method,
        languageCode,
        addressInput: {
          mailerContactName: contactName
        }
      };
      if (isServiceAreaLocation(googleLocation)) {
        body.context = {
          address: verifyOption.addressData.address
        };
      }
      return body;
    }

    default:
      throw new Error('Invalid verification method.');
  }
}

export async function completeVerification({ accessToken, pendingVerification, pin }) {

  const body = { pin };

  const { verification, error } = await fetchEndpoint({
    accessToken,
    method:   'POST',
    endpoint: `${pendingVerification.name}:complete`,
    body:     JSON.stringify(body)
  });

  if (error)
    throw new Error(error.message || 'An unknown error occurred.');

  if (!verification)
    throw new Error('No verification returned.');

  if (verification.state !== verificationStates.COMPLETED)
    throw new Error('Verification was not completed.');

  return verification;
}


// - GMB API helper

async function fetchEndpoint({
  accessToken,
  endpoint,
  body,
  transform = data => data,
  method = 'GET'
}) {
  const url     = `${gmbApiUrl}/${endpoint}`;
  const options = {
    method,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body
  };

  const response = await fetch(url, options);
  const json     = await response.json();
  return transform(json);
}

export const locationSources = enums([
  'gmbAccountLocations'
]);

export function mapAccountLocationToResult(location) {
  return {
    location,
    source: locationSources.gmbAccountLocations
  };
}

export function filterAllowedLocations(location) {
  // don't allow duplicate locations to be connected
  const disallowed = getGoogleLocationIsDuplicate(location);
  return !disallowed;
}
